/**
 * Profile Completeness Calculator
 *
 * Calculates how complete the user's profile is for generating
 * a high-quality personalized journey.
 */

import type { CalculatorState } from '@/types'

export interface CompletenessItem {
  id: string
  label: string
  category: 'profile' | 'income' | 'assets' | 'safety'
  isComplete: boolean
  impact: 'critical' | 'high' | 'medium'
  wizardStep: 1 | 2 | 3 | 4 | 5
  tip?: string
}

export interface ProfileCompleteness {
  percentage: number
  score: number // out of 100
  totalItems: number
  completedItems: number
  items: CompletenessItem[]
  criticalMissing: CompletenessItem[]
  highImpactMissing: CompletenessItem[]
  isReadyForGeneration: boolean
}

/**
 * Calculate profile completeness for journey generation
 */
export function calculateProfileCompleteness(state: CalculatorState): ProfileCompleteness {
  const items: CompletenessItem[] = []

  // ========================================
  // Profile Basics (Step 1) - Critical
  // ========================================

  items.push({
    id: 'current_age',
    label: 'Current age',
    category: 'profile',
    isComplete: state.profile.currentAge > 0 && state.profile.currentAge !== 35, // 35 is default
    impact: 'high',
    wizardStep: 1,
    tip: 'Helps calculate your timeline and retirement projections',
  })

  items.push({
    id: 'nri_since_year',
    label: 'NRI since year',
    category: 'profile',
    isComplete: !!state.profile.nriSinceYear && state.profile.nriSinceYear > 1990,
    impact: 'critical',
    wizardStep: 1,
    tip: 'Required for RNOR eligibility calculation',
  })

  items.push({
    id: 'return_year',
    label: 'Expected return year',
    category: 'profile',
    isComplete: !!state.profile.expectedReturnYear && state.profile.expectedReturnYear >= new Date().getFullYear(),
    impact: 'critical',
    wizardStep: 1,
    tip: 'Required for timeline and tax planning',
  })

  items.push({
    id: 'resident_country',
    label: 'Current country',
    category: 'profile',
    isComplete: !!state.profile.residentCountry,
    impact: 'critical',
    wizardStep: 1,
    tip: 'Determines tax rules and DTAA benefits',
  })

  items.push({
    id: 'marital_status',
    label: 'Marital status',
    category: 'profile',
    isComplete: !!state.profile.maritalStatus,
    impact: 'medium',
    wizardStep: 1,
    tip: 'Affects tax filing and insurance recommendations',
  })

  // ========================================
  // Location (Step 2) - High
  // ========================================

  items.push({
    id: 'target_city',
    label: 'Target city in India',
    category: 'profile',
    isComplete: !!state.location.primaryCityId,
    impact: 'high',
    wizardStep: 2,
    tip: 'Helps estimate cost of living and housing costs',
  })

  items.push({
    id: 'lifestyle',
    label: 'Lifestyle preference',
    category: 'profile',
    isComplete: !!state.location.lifestyle,
    impact: 'medium',
    wizardStep: 2,
    tip: 'Adjusts expense projections for your comfort level',
  })

  // ========================================
  // Income & Expenses (Step 3) - Critical
  // ========================================

  items.push({
    id: 'annual_income',
    label: 'Annual income',
    category: 'income',
    isComplete: state.incomeExpenses.annualIncomeUSD > 0,
    impact: 'critical',
    wizardStep: 3,
    tip: 'Required for FIRE calculations and savings projections',
  })

  items.push({
    id: 'monthly_expenses',
    label: 'Monthly expenses',
    category: 'income',
    isComplete: state.incomeExpenses.monthlyExpensesUSD > 0,
    impact: 'critical',
    wizardStep: 3,
    tip: 'Required for FIRE number and runway calculations',
  })

  items.push({
    id: 'rsu_income',
    label: 'RSU/Stock compensation',
    category: 'income',
    isComplete: state.incomeExpenses.annualRSUIncomeUSD !== undefined,
    impact: 'high',
    wizardStep: 3,
    tip: 'Important for tax optimization strategies',
  })

  // ========================================
  // Assets (Step 4) - High
  // ========================================

  // Check if any resident country assets are filled (country-agnostic)
  const residentAssets = state.residentCountryAssets.assets
  const hasResidentAssets =
    (residentAssets.balance401k || 0) > 0 ||
    (residentAssets.balanceRothIRA || 0) > 0 ||
    (residentAssets.balanceTraditionalIRA || 0) > 0 ||
    (residentAssets.taxableBrokerage || 0) > 0 ||
    (residentAssets.savingsChecking || 0) > 0 ||
    (residentAssets.esppBalance || 0) > 0

  items.push({
    id: 'usa_assets',
    label: 'USA/Resident country assets',
    category: 'assets',
    isComplete: hasResidentAssets,
    impact: 'high',
    wizardStep: 4,
    tip: 'Enables tax-optimized liquidation sequence',
  })

  // Check retirement accounts specifically (country-agnostic)
  const hasRetirementAccounts =
    (residentAssets.balance401k || 0) > 0 ||
    (residentAssets.balanceRothIRA || 0) > 0 ||
    (residentAssets.balanceTraditionalIRA || 0) > 0

  items.push({
    id: 'retirement_accounts',
    label: 'Retirement accounts (401k, IRA)',
    category: 'assets',
    isComplete: hasRetirementAccounts,
    impact: 'high',
    wizardStep: 4,
    tip: 'Critical for DTAA benefits and withdrawal strategies',
  })

  // Check if any India assets are filled
  const hasIndiaAssets =
    state.indiaAssets.npsBalance > 0 ||
    state.indiaAssets.epfBalance > 0 ||
    state.indiaAssets.ppfBalance > 0 ||
    state.indiaAssets.mutualFunds > 0 ||
    state.indiaAssets.realEstateValue > 0 ||
    state.indiaAssets.nreAccountBalance > 0

  items.push({
    id: 'india_assets',
    label: 'India assets (if any)',
    category: 'assets',
    isComplete: hasIndiaAssets || true, // Not critical if none, so mark as complete
    impact: 'medium',
    wizardStep: 4,
    tip: 'Helps with repatriation and investment planning',
  })

  // Check for real estate (country-agnostic)
  const hasRealEstate =
    (residentAssets.primaryHomeEquity || 0) > 0 ||
    (residentAssets.rentalPropertyEquity || 0) > 0 ||
    state.indiaAssets.realEstateValue > 0

  items.push({
    id: 'real_estate',
    label: 'Real estate holdings',
    category: 'assets',
    isComplete: hasRealEstate || true, // Mark complete if explicitly set to 0
    impact: 'medium',
    wizardStep: 4,
    tip: 'Important for capital gains and timing decisions',
  })

  // ========================================
  // Safety Net (Step 5) - Medium
  // ========================================

  items.push({
    id: 'emergency_fund',
    label: 'Emergency fund target',
    category: 'safety',
    isComplete: state.safetyNet.emergencyFundMonths > 0,
    impact: 'medium',
    wizardStep: 5,
    tip: 'Ensures adequate runway during transition',
  })

  items.push({
    id: 'insurance',
    label: 'Insurance coverage',
    category: 'safety',
    isComplete: state.safetyNet.termInsuranceCoverINR > 0 || state.safetyNet.healthInsuranceCoverINR > 0,
    impact: 'medium',
    wizardStep: 5,
    tip: 'Important for risk management recommendations',
  })

  // ========================================
  // Calculate scores
  // ========================================

  // Weighted scoring: critical = 3, high = 2, medium = 1
  const weights = { critical: 3, high: 2, medium: 1 }

  let totalWeight = 0
  let completedWeight = 0

  items.forEach(item => {
    const weight = weights[item.impact]
    totalWeight += weight
    if (item.isComplete) {
      completedWeight += weight
    }
  })

  const completedItems = items.filter(i => i.isComplete).length
  const percentage = Math.round((completedWeight / totalWeight) * 100)

  const criticalMissing = items.filter(i => !i.isComplete && i.impact === 'critical')
  const highImpactMissing = items.filter(i => !i.isComplete && i.impact === 'high')

  // Ready for generation if no critical items missing
  const isReadyForGeneration = criticalMissing.length === 0

  return {
    percentage,
    score: percentage,
    totalItems: items.length,
    completedItems,
    items,
    criticalMissing,
    highImpactMissing,
    isReadyForGeneration,
  }
}

/**
 * Get a friendly message based on completeness
 */
export function getCompletenessMessage(completeness: ProfileCompleteness): string {
  if (completeness.percentage >= 90) {
    return "Your profile is comprehensive! You'll get the most personalized journey."
  } else if (completeness.percentage >= 70) {
    return "Good profile coverage. Add a few more details for better recommendations."
  } else if (completeness.percentage >= 50) {
    return "Add more details for a more personalized and actionable journey."
  } else {
    return "Complete your profile to unlock personalized recommendations."
  }
}

/**
 * Get the most important missing item
 */
export function getMostImportantMissing(completeness: ProfileCompleteness): CompletenessItem | null {
  if (completeness.criticalMissing.length > 0) {
    return completeness.criticalMissing[0]!
  }
  if (completeness.highImpactMissing.length > 0) {
    return completeness.highImpactMissing[0]!
  }
  const mediumMissing = completeness.items.filter(i => !i.isComplete && i.impact === 'medium')
  return mediumMissing[0] || null
}
